import React, { useState } from 'react';
import { Stack, Typography, useTheme, Box, Link, Dialog, DialogTitle, DialogContent, IconButton } from '@mui/material';
import { Close } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Headphones } from 'lucide-react';
import { useBrandConfig } from '../stores/brandConfigContext';
import { 
  FacebookIcon, 
  InstagramIcon, 
  LinkedinIcon,
  XIcon, 
  YoutubeIcon, 
  TiktokIcon, 
  LukaLogoIcon 
} from '../components/onboarding-icons';

// Mapeo de plataforma a componente de icono
const socialIconMap: Record<string, React.FC<any>> = {
  facebook: FacebookIcon,
  instagram: InstagramIcon,
  linkedin: LinkedinIcon,
  x: XIcon,
  youtube: YoutubeIcon,
  tiktok: TiktokIcon,
};

const LoginFooter: React.FC = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { config } = useBrandConfig();

  // Obtener redes sociales del config, o array vacío si no hay
  const redesSociales = config?.redesSociales || [];
  
  // Obtener URL o texto de términos y condiciones
  const terminosUrl = config?.terminosCondiciones;
  const isTermsUrl = terminosUrl?.startsWith('http://') || terminosUrl?.startsWith('https://');
  const [showTermsDialog, setShowTermsDialog] = useState(false);
  
  // Determinar si no hay redes ni términos
  const noHayRedesNiTerminos = redesSociales.length === 0 && !terminosUrl;

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{
        width: '100%',
        height: { xs: 'auto', md: '60px' },
        padding: { xs: '16px 20px 16px 45px', md: '15px 25px 15px 50px' },
        backgroundColor: theme.palette.background.paper,
      }}
    >
      {/* Social Media Icons - Dinámicos */}
      <Stack 
        direction="row" 
        alignItems="center" 
        spacing={3}
        sx={{ display: { xs: 'none', md: 'flex' } }}
      >
        {redesSociales.map((red, index) => {
          const IconComponent = socialIconMap[red.Plataforma.toLowerCase()];
          if (!IconComponent) return null;
          
          return (
            <Box
              key={index}
              component="a"
              href={red.Url}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                padding: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: theme.palette.text.secondary,
                textDecoration: 'none',
                '&:hover': {
                  color: theme.palette.primary.main,
                }
              }}
            >
              <IconComponent fontSize="small" height={16} />
            </Box>
          );
        })}
        
        {/* Divider - solo si hay redes sociales */}
        {redesSociales.length > 0 && (
          <Box
            sx={{
              height: 20,
              width: 2,
              backgroundColor: theme.palette.divider,
              mx: 1
            }}
          />
        )}
        
        {/* Términos y Condiciones - solo si hay URL */}
        {terminosUrl && (
          <>
            {isTermsUrl ? (
              <Link
                href={terminosUrl}
                target="_blank"
                rel="noopener noreferrer"
                underline="none"
                sx={{
                  color: 'rgb(172, 185, 201)',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  '&:hover': {
                    color: theme.palette.primary.main,
                  }
                }}
              >
                {t('common.termsAndConditions')}
              </Link>
            ) : (
              <Typography
                variant="body2"
                onClick={() => setShowTermsDialog(true)}
                sx={{
                  color: 'rgb(172, 185, 201)',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  '&:hover': {
                    color: theme.palette.primary.main,
                  }
                }}
              >
                {t('common.termsAndConditions')}
              </Typography>
            )}
            
            {/* Divider entre T&C y Soporte */}
            <Box
              sx={{
                height: 20,
                width: 2,
                backgroundColor: theme.palette.divider,
                mx: 1
              }}
            />
          </>
        )}
        
        {/* Soporte al Cliente - con icono si no hay redes ni términos */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            cursor: 'pointer',
            color: 'rgb(172, 185, 201)',
            '&:hover': {
              color: theme.palette.primary.main,
            }
          }}
        >
          {noHayRedesNiTerminos && (
            <Headphones size={16} />
          )}
          <Typography
            variant="body2"
            sx={{
              color: 'inherit',
            }}
          >
            {t('common.customerSupport')}
          </Typography>
        </Box>
      </Stack>

      {/* Logo */}
      <Stack 
        alignItems="center"
        justifyContent="center"
        sx={{
          height: '100%'
        }}
      >
        <LukaLogoIcon fontSize="small" height={30} />
      </Stack>
      {/* Dialog de Términos y Condiciones (cuando no es URL) */}
      <Dialog
        open={showTermsDialog}
        onClose={() => setShowTermsDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
          <Typography sx={{ fontWeight: 600, fontSize: '18px', lineHeight: '28px', color: theme.palette.text.primary }}>
            {t('common.termsAndConditions')}
          </Typography>
          <IconButton onClick={() => setShowTermsDialog(false)} size="small">
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', textAlign: 'justify' }}>
            {terminosUrl}
          </Typography>
        </DialogContent>
      </Dialog>
    </Stack>
  );
};

export default LoginFooter;
